import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { motion } from 'motion/react';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { useEffect } from 'react';

interface Props {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  goalValuesLength: number;
}

const WeeklyGoalPassedSkillModal: React.FC<Props> = ({ visible, setVisible, goalValuesLength }) => {
  useEffect(() => {
    const autoDismiss = setTimeout(() => {
      setVisible(false);
    }, 6500);
    // Without this, dismissing via Skip leaves the timer armed and it fires into an unmounted modal.
    return () => clearTimeout(autoDismiss);
  }, []);

  return (
    <Dialog
      className="tw-w-[1000px] tw-rounded-lg tw-h-[500px]"
      visible={visible}
      onHide={() => setVisible(false)}
      footer={<Button type="button" label="Skip" aria-label="Skip" onClick={() => setVisible(false)} />}
      pt={{
        content: { className: 'tw-flex tw-justify-center' },
        footer: { className: '!tw-justify-center' },
        root: { className: '!tw-max-h-[85%]' },
      }}
    >
      <div className="tw-flex tw-flex-col tw-p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{
            duration: 1,
            delay: 0.5,
            ease: [0.34, 1.56, 0.64, 1],
          }}
        >
          <h1 className="tw-text-center tw-font-bold tw-text-2xl tw-leading-8 tw-text-gray-900 tw-font-sora">
            New skill earned!
          </h1>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 1, ease: [0.34, 1.56, 0.64, 1] }}
          className="tw-w-[500px] tw-h-[250px]"
        >
          {goalValuesLength === 1 && <DotLottieReact src={'/weekly-goal-passed-1-skill.json'} autoplay loop={false} />}
          {goalValuesLength === 2 && <DotLottieReact src={'/weekly-goal-passed-2-skill.json'} autoplay loop={true} />}
          {goalValuesLength >= 3 && (
            <DotLottieReact src={'/weekly-goal-passed-extra-skill.json'} autoplay loop={false} />
          )}
        </motion.div>
        <motion.div
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 4, ease: [0.34, 1.56, 0.64, 1] }}
          className="tw-relative tw-bottom-[50px]"
        >
          {goalValuesLength === 1 && (
            <p className="tw-text-center tw-text-gray-600 tw-text-base tw-font-atkinson tw-m-0 tw-font-bold">
              You&apos;re one skill away from your <span className="tw-font-bold">weekly goal</span>!
            </p>
          )}
          {goalValuesLength === 2 && (
            <div className="tw-flex tw-flex-col tw-gap-y-2">
              <h1 className="tw-text-center tw-text-gray-600 tw-text-base tw-font-atkinson tw-m-0 tw-font-bold">
                You Nailed your Weekly Goal!
              </h1>
              <p className="tw-text-center tw-text-gray-600 tw-text-base tw-font-atkinson tw-m-0 tw-font-bold">
                Keep your rhythm going. Why stop?
              </p>
            </div>
          )}
          {goalValuesLength >= 3 && (
            <div className="tw-flex tw-flex-col tw-gap-y-2">
              <h1 className="tw-text-center tw-text-gray-600 tw-text-base tw-font-atkinson tw-m-0 tw-font-bold">
                You rocked your Weekly Goal!
              </h1>
              <p className="tw-text-center tw-text-gray-600 tw-text-base tw-font-atkinson tw-m-0 tw-font-bold">
                You&apos;re on a roll! Keep it up!
              </p>
            </div>
          )}
        </motion.div>
      </div>
    </Dialog>
  );
};

export default WeeklyGoalPassedSkillModal;
